import { watch } from 'vue'
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'

import CatalogPage from '@/catalog/CatalogPage.vue'
import { useMaintenance } from '@/maintenance/useMaintenance'
import { GGVIE_STANDALONE_URL } from '@/shared/config'

import { fetchMe, onSessionExpired } from './auth/api'
import { postLoginRoute } from './auth/landing'
import type { AuthUser } from './auth/types'
import { useAuth } from './auth/useAuth'

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: CatalogPage,
      meta: { requiresAuth: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/auth/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/request-access',
      name: 'request-access',
      component: () => import('@/auth/RequestAccessView.vue'),
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/auth/ForgotPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/set-password/:token',
      name: 'set-password',
      component: () => import('@/auth/SetPasswordView.vue'),
      meta: { public: true },
      props: true,
    },
    {
      path: '/forgot-password/:token',
      name: 'reset-password',
      component: () => import('@/auth/SetPasswordView.vue'),
      meta: { public: true },
      props: true,
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/admin/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
      redirect: '/admin/users',
      children: [
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/admin/UsersListView.vue'),
        },
        {
          path: 'users/:userId',
          name: 'admin-user-detail',
          component: () => import('@/admin/UserDetailView.vue'),
          props: true,
        },
        {
          path: 'pending',
          name: 'admin-pending',
          component: () => import('@/admin/PendingRequestsView.vue'),
        },
        {
          path: 'maintenance',
          name: 'admin-maintenance',
          component: () => import('@/admin/MaintenanceAdminView.vue'),
        },
        {
          path: 'admission',
          name: 'admin-admission',
          component: () => import('@/admin/AdmissionSettingsView.vue'),
        },
        {
          path: 'lab',
          name: 'admin-lab',
          component: () => import('@/admin/LabAdminView.vue'),
        },
      ],
    },
    {
      path: '/admin/validate/:token',
      name: 'admin-validate',
      component: () => import('@/admin/AdminValidateView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
      props: true,
    },
    {
      path: '/vue/chat',
      name: 'vue-chat',
      component: () => import('@sapiendo/lead/LeadChatView.vue'),
      meta: { requiresAuth: true, agent: 'lead' },
    },
    {
      path: '/vue/consulting',
      name: 'vue-consulting',
      component: () => import('@sapiendo/consulting/ConsultingChatView.vue'),
      meta: { requiresAuth: true, agent: 'consulting' },
    },
    {
      path: '/vue/knowledge',
      name: 'vue-knowledge',
      component: () => import('@/knowledge/KnowledgeTestView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval',
      name: 'eval-landing',
      component: () => import('@/eval/EvalLandingView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/judge',
      name: 'eval',
      component: () => import('@/eval/EvalView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/runs/:runId',
      name: 'eval-run-detail',
      component: () => import('@/eval/EvalRunDetailView.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/datasets/:datasetId',
      name: 'eval-dataset-detail',
      component: () => import('@/eval/EvalDatasetDetailView.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/compare',
      name: 'eval-compare',
      component: () => import('@/eval/EvalCompareView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/extraction',
      name: 'eval-gathering',
      component: () => import('@/eval/EvalGatheringView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/extraction/datasets/:name',
      name: 'eval-gathering-dataset',
      component: () => import('@/eval/EvalGatheringDatasetView.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/trajectories',
      name: 'eval-trajectories',
      component: () => import('@/eval/EvalTrajectoryView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/trajectories/batches',
      name: 'eval-trajectory-batches',
      component: () => import('@/eval/EvalTrajectoryBatchesView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/trajectories/batches/:batchId',
      name: 'eval-trajectory-batch-detail',
      component: () => import('@/eval/EvalTrajectoryBatchDetailView.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/trajectories/batches/:baseId/compare/:targetId',
      name: 'eval-trajectory-batch-compare',
      component: () => import('@/eval/EvalTrajectoryCompareView.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/extraction/runs/:runId',
      name: 'eval-gathering-run-detail',
      component: () => import('@/eval/EvalGatheringRunDetailView.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/eval/extraction/compare',
      name: 'eval-gathering-compare',
      component: () => import('@/eval/EvalGatheringCompareView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/feedbacks',
      name: 'feedbacks',
      component: () => import('@/feedbacks/FeedbacksView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      // SD-3875 — la route /ggvie charge désormais l'app standalone src/ggvie/.
      // C'est l'app cible (build:ggvie). Code 100% isolé de la sandbox.
      path: '/ggvie/:conversationId?',
      name: 'ggvie',
      // SD-3875 — la route /ggvie charge la SPA standalone src/agents/ggvie/consulting/.
      component: () => import('@ggvie-consulting/consulting/GGVIEChatView.vue'),
      props: true,
      meta: { requiresAuth: true, agent: 'consulting_ggvie' },
    },
    {
      // Agent Sapiendo PER — agent non brandé, accessible depuis l'interface Sapiendo
      // (carte dans /agents). Code autonome : `sandbox/src/agents/sapiendo/per/`
      // + `src/agent/agents/sapiendo_per/`.
      path: '/sapiendo/per/:conversationId?',
      name: 'sapiendo-per',
      component: () => import('@sapiendo-per/view/SapiendoPerChatView.vue'),
      props: true,
      meta: { requiresAuth: true, agent: 'sapiendo_per' },
    },
    {
      // SD-3875 — page de catalogue des agents, groupés par client.
      path: '/agents',
      name: 'agents',
      component: () => import('@/catalog/AgentsListView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // SD-4367 — page exclusive pendant une maintenance active (non-admins).
      path: '/maintenance',
      name: 'maintenance',
      component: () => import('@/maintenance/MaintenanceView.vue'),
      meta: { public: true },
    },
    {
      // Catch-all : toute route inconnue (incluant les anciennes routes
      // /agents, /per/*, /react/chat supprimées en SD-3875) redirige vers
      // la page d'accueil (CatalogPage).
      path: '/:pathMatch(.*)*',
      redirect: { name: 'home' },
    },
  ],
})

// SD-4253 — quand la session expire (refresh KO sur 401), `authFetch` émet
// `sessionExpired` : on redirige automatiquement vers /login en préservant
// l'URL courante dans `?redirect=` + `?reason=session_expired`. C'est la SOURCE
// UNIQUE de redirection sur session perdue ; les watchdogs/guards se contentent
// d'appeler `fetchMe()` (qui passe par `authFetch` → emit). Évite qu'un conseiller
// reste devant une page muette sans réaliser qu'il a été déconnecté.
onSessionExpired(() => {
  const current = router.currentRoute.value
  if (current.meta.public) return
  if (current.name === 'login') return
  void router.push({
    name: 'login',
    query: { redirect: current.fullPath, reason: 'session_expired' },
  })
})

/**
 * Repli d'une navigation refusée (droits, fin de maintenance sans route mémorisée) : la page d'arrivée de
 * l'utilisateur — la vue de son agent quand il n'en a qu'un, la page des agents sinon, l'accueil pour un admin —
 * plutôt que le catalogue d'accueil, qui n'est pas la page d'un conseiller.
 */
function fallbackRoute(auth: ReturnType<typeof useAuth>): string {
  return postLoginRoute(null, auth.isAdmin.value, auth.user.value?.agents ?? [])
}

/** Signature d'un conseiller GGVie : non admin, restreint au seul agent consulting_ggvie. */
function isGgvieOnlyAdvisor(user: AuthUser | null): boolean {
  return !!user && !user.is_admin && user.agents.length === 1 && user.agents[0] === 'consulting_ggvie'
}

// SD-4367 — bascule maintenance HORS navigation : quand le statut effectif passe à
// `active` (poll 10 s, tick local à 0:00, ou 503-marqueur), on redirige immédiatement
// le non-admin vers la page maintenance en mémorisant sa route pour la restauration.
const maintenance = useMaintenance()
watch(maintenance.status, (status) => {
  const current = router.currentRoute.value
  if (status === 'active' && !useAuth().isAdmin.value && !current.meta.public && current.name !== 'maintenance') {
    maintenance.saveReturnTo(current.fullPath)
    void router.push({ name: 'maintenance' })
  }
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  // SD-4367 — le guard doit connaître l'état maintenance dès le premier rendu
  // (promesse memoizée, un seul fetch au boot, ne rejette jamais).
  await maintenance.ensureLoaded()

  const auth = useAuth()

  // Conseiller GGVie : interdit d'accès à la sandbox. Toute route (même /login une fois
  // authentifié, sauf en impersonation) le renvoie vers le front standalone GGVie.
  if (
    GGVIE_STANDALONE_URL &&
    auth.isAuthenticated.value &&
    !auth.isImpersonating.value &&
    isGgvieOnlyAdvisor(auth.user.value)
  ) {
    window.location.href = GGVIE_STANDALONE_URL
    return false
  }

  // SD-4367 — maintenance active : un non-admin n'accède qu'à la page maintenance
  // (et aux routes publiques comme /login — un non-admin connecté y est renvoyé
  // ici à sa prochaine navigation). Quand la maintenance est terminée, la page
  // maintenance restaure la route mémorisée.
  if (maintenance.status.value === 'active' && !auth.isAdmin.value) {
    if (to.name !== 'maintenance' && !to.meta.public) {
      maintenance.saveReturnTo(to.fullPath)
      return { name: 'maintenance' }
    }
  } else if (to.name === 'maintenance') {
    return maintenance.consumeReturnTo() ?? fallbackRoute(auth)
  }

  const isPublic = Boolean(to.meta.public)
  const requiresAuth = Boolean(to.meta.requiresAuth)
  const requiresAdmin = Boolean(to.meta.requiresAdmin)
  const requiredAgent = (to.meta.agent as string | undefined) || null

  if (isPublic) return true

  if (!auth.isAuthenticated.value) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Vérifie la session côté serveur à chaque navigation.
  // Si le token est expiré ou révoqué, redirige vers login.
  try {
    await fetchMe()
  } catch {
    // fetchMe a déjà clearSession() sur 401
  }
  if (!auth.isAuthenticated.value) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (requiresAdmin && !auth.isAdmin.value) {
    return fallbackRoute(auth)
  }

  if (requiredAgent && !auth.isAdmin.value) {
    const agents = auth.user.value?.agents ?? []
    if (!agents.includes(requiredAgent)) {
      return fallbackRoute(auth)
    }
  }

  return true
})
